import type { CalculationLine } from '../releases-calculate.ts';
import { type CalendarType, UnitsTracker } from '../formulas/units.ts';
import {
  type ColumnDescriptor,
  type ColumnID,
  type ColumnValue,
  type Row,
  type Table,
  type TableID,
  toPrimitive,
  sourceForAllColumns,
} from './report-pipelines.ts';
import type { EmissionsPeriodDetails } from '../emissions/emissions-dates.ts';
import type { CasOrCategory, EmissionsTrackingFrequency, EmissionsUnitID, UsageUnits } from '../../types/db.ts';
import type { ChemicalBrief, FacilityProductBrief, Warning } from '../../types/tri.ts';
import type { EmissionUnitTypeWithConfig, EmissionUnitWithConfig } from '../../types/emissions.ts';
import {
  type EmissionsAnswerByPeriodAndQuestion,
  type EmissionsProcessAndProfiles,
  type EmissionsQuestionAnnotated,
} from '../emissions/emissions-questionnaire.ts';
import { CalendarValue } from '../formulas/ast.ts';
import { emissionsStandardPollutants } from '../emissions/emissions-standard-pollutants.ts';
import type { FormulaOutputType } from '../../types/release-profiles.ts';

export interface EmissionsProductUsageItem {
  scopeLabel: string;
  period: EmissionsPeriodDetails;
  product: FacilityProductBrief;
  units: UsageUnits;
  value: number;
}

export interface EmissionsUnitWithQuestionAndAnswerData {
  emissionsUnitID: EmissionsUnitID;
  unit: EmissionUnitWithConfig;
  unitType: EmissionUnitTypeWithConfig;
  periods: EmissionsPeriodDetails[];
  periodsUsing: EmissionsPeriodDetails[];
  process: EmissionsProcessAndProfiles;
  chemicals: Set<CasOrCategory>;
  answersFromUnit: EmissionsAnswerByPeriodAndQuestion;
  answersFromFacility: EmissionsAnswerByPeriodAndQuestion;
  answersFromUnitType: EmissionsAnswerByPeriodAndQuestion;
  questions: EmissionsQuestionAnnotated[];
}

export interface EmissionsCalculationResultsForPeriod {
  unit: EmissionsUnitWithQuestionAndAnswerData;
  period: EmissionsPeriodDetails;
  questions: EmissionsQuestionAnnotated[];
  warnings: Warning[];
  calculationLines: CalculationLine[];
}

export interface EmissionsCalculationLineForReport {
  unit: EmissionsUnitWithQuestionAndAnswerData;
  period: EmissionsPeriodDetails;
  line: CalculationLine;
  values: Row['values'];
}

function periodToColumnValue(
  calendarType: CalendarType,
  period: EmissionsPeriodDetails,
): {
  type: 'calendarValue';
  value: CalendarValue['value'];
} {
  return {
    type: 'calendarValue',
    value:
      // For reporting purposes we use the parent period to avoid the user needing to go back and add period conversions
      // whenever they add a split period:
      'parentPeriod' in period
        ? {
            calendarType,
            start: period.parentPeriod.start,
            end: period.parentPeriod.end,
            key: period.parentPeriod.period,
          }
        : {
            calendarType,
            start: period.start,
            end: period.end,
            key: period.period,
          },
  };
}

function emissionsPeriodFrequencyToCalendarType(frequency: EmissionsTrackingFrequency): CalendarType {
  if (frequency === 'daily') {
    return 'date';
  } else if (frequency === 'weekly') {
    return 'week';
  } else if (frequency === 'monthly') {
    return 'month';
  } else if (frequency === 'quarterly') {
    return 'quarter';
  } else if (frequency === 'semiannually') {
    return 'halfYear';
  } else if (frequency === 'annually') {
    return 'year';
  } else {
    const unexpected: never = frequency;
    throw new Error(`Unexpected emissions period frequency: ${unexpected}`);
  }
}

export function productsAndChemicalsToTable(
  products: FacilityProductBrief[],
  tableID: TableID,
  chemicals: Partial<Record<CasOrCategory, { isHap: boolean }>>,
): Table {
  // TODO: 1. Emissions - Need better UI for percentages (i.e. display as 1% instead of 0.01)
  // TODO: 1. Emissions - Previously we decided in ast.ts to convert all percentages to scalars so that they would not cause unit conversion errors. Now, this seems problematic as we cannot record `Concentration > 1 %` in the reporting UI as `Concentration` stays a percentage but `1 %` becomes 0.01 (unitless).
  const percentageUnits = UnitsTracker.fromParsedUnitString('%');
  const columns = new Map<ColumnID, ColumnDescriptor>([
    [
      'ProductName' as ColumnID,
      {
        columnID: 'ProductName' as ColumnID,
        type: 'string',
      },
    ],
    [
      'SpecificGravity' as ColumnID,
      {
        columnID: 'SpecificGravity' as ColumnID,
        type: 'number',
        units: UnitsTracker.unitless(),
      },
    ],
    [
      'SolidsPercentageByWeight' as ColumnID,
      {
        columnID: 'SolidsPercentageByWeight' as ColumnID,
        type: 'number',
        units: percentageUnits,
      },
    ],
    [
      'SolidsPercentageByVolume' as ColumnID,
      {
        columnID: 'SolidsPercentageByVolume' as ColumnID,
        type: 'number',
        units: percentageUnits,
      },
    ],
    [
      'Chemical' as ColumnID,
      {
        columnID: 'Chemical' as ColumnID,
        type: 'casOrCategory',
      },
    ],
    [
      'HAP' as ColumnID,
      {
        columnID: 'HAP' as ColumnID,
        type: 'boolean',
      },
    ],
    [
      'Concentration' as ColumnID,
      {
        columnID: 'Concentration' as ColumnID,
        type: 'number',
        units: percentageUnits,
      },
    ],
  ]);

  const rows: Row[] = products.flatMap((product) => {
    return product.chemicals.map((chemical): Row => {
      const values: Row['values'] = new Map();

      values.set('ProductName' as ColumnID, { type: 'string', value: product.name });

      if (product.specificGravity !== null) {
        values.set('SpecificGravity' as ColumnID, {
          type: 'number',
          units: UnitsTracker.unitless(),
          value: product.specificGravity,
        });
      }

      if (product.solidsPercentageByWeight !== null) {
        values.set('SolidsPercentageByWeight' as ColumnID, {
          type: 'number',
          units: percentageUnits,
          value: product.solidsPercentageByWeight,
        });
      }

      if (product.solidsPercentageByVolume !== null) {
        values.set('SolidsPercentageByVolume' as ColumnID, {
          type: 'number',
          units: percentageUnits,
          value: product.solidsPercentageByVolume,
        });
      }

      values.set('Chemical' as ColumnID, {
        type: 'casOrCategory',
        casOrCategory: chemical.casOrCategoryOriginal,
      });

      values.set('HAP' as ColumnID, {
        type: 'boolean',
        value: !!chemicals[chemical.casOrCategoryOriginal]?.isHap,
      });

      values.set('Concentration' as ColumnID, {
        type: 'number',
        units: percentageUnits,
        value: chemical.concentrationComputed,
      });

      return { values, source: sourceForAllColumns(values, [{ product, chemical }]) };
    });
  });

  return {
    tableID,
    columns,
    rows,
  };
}

export function productUsageToTable(
  frequency: EmissionsTrackingFrequency,
  usage: EmissionsProductUsageItem[],
  tableID: TableID,
): Table {
  const calendarType = emissionsPeriodFrequencyToCalendarType(frequency);
  const percentageUnits = UnitsTracker.fromParsedUnitString('%');
  const poundsUnits = UnitsTracker.fromParsedUnitString('pounds');
  const gallonsUnits = UnitsTracker.fromParsedUnitString('gallons');
  const columns = new Map<ColumnID, ColumnDescriptor>([
    [
      'Period' as ColumnID,
      {
        columnID: 'Period' as ColumnID,
        type: 'calendarValue',
        calendarType,
      },
    ],
    [
      'Scope' as ColumnID,
      {
        columnID: 'Scope' as ColumnID,
        type: 'string',
      },
    ],
    [
      'ProductName' as ColumnID,
      {
        columnID: 'ProductName' as ColumnID,
        type: 'string',
      },
    ],
    [
      'SpecificGravity' as ColumnID,
      {
        columnID: 'SpecificGravity' as ColumnID,
        type: 'number',
        units: UnitsTracker.unitless(),
      },
    ],
    [
      'SolidsPercentageByWeight' as ColumnID,
      {
        columnID: 'SolidsPercentageByWeight' as ColumnID,
        type: 'number',
        units: percentageUnits,
      },
    ],
    [
      'SolidsPercentageByVolume' as ColumnID,
      {
        columnID: 'SolidsPercentageByVolume' as ColumnID,
        type: 'number',
        units: percentageUnits,
      },
    ],
    [
      'PoundsUsed' as ColumnID,
      {
        columnID: 'PoundsUsed' as ColumnID,
        type: 'number',
        units: poundsUnits,
      },
    ],
    [
      'GallonsUsed' as ColumnID,
      {
        columnID: 'GallonsUsed' as ColumnID,
        type: 'number',
        units: gallonsUnits,
      },
    ],
  ]);

  let hasPounds = false;
  let hasGallons = false;

  const rows: Row[] = usage.map((item) => {
    const values: Row['values'] = new Map();

    values.set('Period' as ColumnID, periodToColumnValue(calendarType, item.period));

    values.set('Scope' as ColumnID, { type: 'string', value: item.scopeLabel });

    values.set('ProductName' as ColumnID, { type: 'string', value: item.product.name });

    if (item.product.specificGravity !== null) {
      values.set('SpecificGravity' as ColumnID, {
        type: 'number',
        units: UnitsTracker.unitless(),
        value: item.product.specificGravity,
      });
    }

    if (item.product.solidsPercentageByWeight !== null) {
      values.set('SolidsPercentageByWeight' as ColumnID, {
        type: 'number',
        units: percentageUnits,
        value: item.product.solidsPercentageByWeight,
      });
    }

    if (item.product.solidsPercentageByVolume !== null) {
      values.set('SolidsPercentageByVolume' as ColumnID, {
        type: 'number',
        units: percentageUnits,
        value: item.product.solidsPercentageByVolume,
      });
    }

    if (item.units === 'pounds') {
      hasPounds = true;
      values.set('PoundsUsed' as ColumnID, {
        type: 'number',
        units: poundsUnits,
        value: item.value,
      });
    } else if (item.units === 'gallons') {
      hasGallons = true;
      values.set('GallonsUsed' as ColumnID, {
        type: 'number',
        units: gallonsUnits,
        value: item.value,
      });

      if (item.product.specificGravity !== null) {
        hasPounds = true;
        values.set('PoundsUsed' as ColumnID, {
          type: 'number',
          units: poundsUnits,
          value: item.value * 8.345 * item.product.specificGravity,
        });
      }
    } else {
      const unexpected: never = item.units;
      throw new Error(`Unexpected product usage units: ${unexpected}`);
    }

    return { values, source: sourceForAllColumns(values, [item]) };
  });

  if (!hasPounds) {
    columns.delete('PoundsUsed' as ColumnID);
  }
  if (!hasGallons) {
    columns.delete('GallonsUsed' as ColumnID);
  }

  return {
    tableID,
    columns,
    rows,
  };
}

const formulaOutputTypeLabels: Record<FormulaOutputType, string> = {
  actual: 'Actual',
  potential: 'Potential',
  complianceNumerator: 'Compliance Metric Numerator',
  complianceDenominator: 'Compliance Metric Denominator',
  tier2Max: 'Tier II Maximum',
  tier2Avg: 'Tier II Average',
};

/**
 * Transform emissions CalculationLine[] into the pipeline Table format.
 *
 * This utility bridges the emissions calculation system with the report pipeline system,
 * allowing users to apply transformations (filter, group, sum, etc.) to their emissions data.
 *
 * Columns created:
 * - Period: The emissions period (e.g., "2024-01")
 * - Kind: "Actual" or "Potential"
 * - ReleaseProfileName: Name of the release profile
 * - FormulaName: Name of the formula used
 * - ProductName: Name of the product (or null if N/A)
 * - PoundsUsed: Pounds of product used (or null if N/A)
 * - Chemical: The CAS number or category (or null if N/A)
 * - Result: The computed result (number with units)
 * - ResultUnits: String representation of the units
 */
export function calculationLinesToTable(
  calculations: EmissionsCalculationResultsForPeriod[],
  tableID: TableID,
  chemicals: Partial<Record<CasOrCategory, { isHap: boolean }>>,
): Table {
  const calendarType = emissionsPeriodFrequencyToCalendarType(
    calculations[0]?.unit.unit.trackingFrequency ?? 'monthly',
  );
  const columns = new Map<ColumnID, ColumnDescriptor>([
    [
      'Period' as ColumnID,
      {
        columnID: 'Period' as ColumnID,
        type: 'calendarValue',
        calendarType,
      },
    ],
    [
      'Unit Type' as ColumnID,
      {
        columnID: 'Unit Type' as ColumnID,
        type: 'string',
      },
    ],
    [
      'Unit' as ColumnID,
      {
        columnID: 'Unit' as ColumnID,
        type: 'string',
      },
    ],
    [
      'Chemical' as ColumnID,
      {
        columnID: 'Chemical' as ColumnID,
        type: 'casOrCategory',
      },
    ],
    [
      'HAP' as ColumnID,
      {
        columnID: 'HAP' as ColumnID,
        type: 'boolean',
      },
    ],
    [
      'Kind' as ColumnID,
      {
        columnID: 'Kind' as ColumnID,
        type: 'string',
      },
    ],
    [
      'ReleaseProfileName' as ColumnID,
      {
        columnID: 'ReleaseProfileName' as ColumnID,
        type: 'string',
      },
    ],
    [
      'FormulaName' as ColumnID,
      {
        columnID: 'FormulaName' as ColumnID,
        type: 'string',
      },
    ],
    [
      'ProductName' as ColumnID,
      {
        columnID: 'ProductName' as ColumnID,
        type: 'string',
      },
    ],
    [
      'PoundsUsed' as ColumnID,
      {
        columnID: 'PoundsUsed' as ColumnID,
        type: 'number',
        units: UnitsTracker.fromParsedUnitString('pounds'),
      },
    ],
  ]);

  const rows: Row[] = calculations.flatMap((calc): Row[] => {
    // Most common source of NaN is incomplete answers, i.e. you have some formula with a ratio made up of user
    // provided numbers but the user has not yet provided those answers:
    const withoutNan = calc.calculationLines.filter((line) => !Number.isNaN(line.computationResult));
    return withoutNan.map((line): Row => {
      const values: Row['values'] = new Map();

      values.set('Period' as ColumnID, periodToColumnValue(calendarType, calc.period));

      values.set('Unit Type' as ColumnID, { type: 'string', value: calc.unit.unitType.name });

      values.set('Unit' as ColumnID, { type: 'string', value: calc.unit.unit.name });

      values.set(
        'Chemical' as ColumnID,
        line.casOrCategoryMapped
          ? {
              type: 'casOrCategory',
              casOrCategory: line.casOrCategoryMapped,
            }
          : null,
      );

      values.set('HAP' as ColumnID, {
        type: 'boolean',
        value: line.casOrCategoryMapped ? !!chemicals[line.casOrCategoryMapped]?.isHap : false,
      });

      values.set('Kind' as ColumnID, {
        type: 'string',
        value: formulaOutputTypeLabels[line.formulaOutputType],
      });

      values.set('ReleaseProfileName' as ColumnID, {
        type: 'string',
        value: line.releaseProfileName,
      });

      values.set('FormulaName' as ColumnID, {
        type: 'string',
        value: line.releaseProfileFormulaName,
      });

      values.set('ProductName' as ColumnID, line.product ? { type: 'string', value: line.product.productName } : null);

      values.set(
        'PoundsUsed' as ColumnID,
        line.product
          ? {
              type: 'number',
              value: line.product.poundsUsed,
              units: UnitsTracker.fromParsedUnitString('pounds'),
            }
          : null,
      );

      for (const [variableName, value] of Object.entries(line.variablesUsed)) {
        if (!value) {
          continue;
        }

        if (!columns.has(variableName as ColumnID)) {
          columns.set(variableName as ColumnID, {
            columnID: variableName as ColumnID,
            type: 'number',
            units: UnitsTracker.fromParsedUnitString(value.units),
          });
        }

        values.set(variableName as ColumnID, {
          type: 'number',
          value: value.value,
          units: UnitsTracker.fromParsedUnitString(value.units),
        });
      }

      const units = UnitsTracker.fromParsedUnitString(line.computationUnits);
      values.set('Result' as ColumnID, {
        type: 'number',
        value: line.computationResult,
        units,
      });

      values.set('ResultUnits' as ColumnID, {
        type: 'units',
        value: units,
      });

      values.set('ResultDimensions' as ColumnID, {
        type: 'string',
        value: units.toDimensionString(),
      });

      return {
        values,
        source: sourceForAllColumns(values, [
          { unit: calc.unit, period: calc.period, line, values } satisfies EmissionsCalculationLineForReport,
        ]),
      };
    });
  });

  // We set these last so they are always the last columns of the table:
  columns.set('Result' as ColumnID, {
    columnID: 'Result' as ColumnID,
    type: 'number',
    units: UnitsTracker.fromParsedUnitString('pounds'),
  });

  columns.set('ResultUnits' as ColumnID, {
    columnID: 'ResultUnits' as ColumnID,
    type: 'units',
  });

  columns.set('ResultDimensions' as ColumnID, {
    columnID: 'ResultDimensions' as ColumnID,
    type: 'string',
  });

  return {
    tableID,
    columns,
    rows,
  };
}

const standardPollutantLabels = new Map(
  emissionsStandardPollutants.map((pollutant) => [pollutant.key, pollutant.label]),
);

export function emissionsFormatCellValue(
  chemicalsByCasOrCategory: Partial<Record<CasOrCategory, ChemicalBrief>>,
  value: ColumnValue | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (value.type === 'number') {
    if (Number.isInteger(value.value)) {
      return value.value.toLocaleString();
    }
    return value.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 5 });
  } else if (value.type === 'boolean') {
    return value.value ? 'Yes' : 'No';
  } else if (value.type === 'calendarValue') {
    // TODO: 1. Emissions - Reports - Need better formatting (i.e. '2024 Q1')
    return value.value.key;
  } else if (value.type === 'casOrCategory') {
    if (standardPollutantLabels.has(value.casOrCategory)) {
      return standardPollutantLabels.get(value.casOrCategory)!;
    } else {
      const maybeName = chemicalsByCasOrCategory[value.casOrCategory]?.name;
      if (maybeName) {
        return `${maybeName} (${value.casOrCategory})`;
      } else {
        return value.casOrCategory;
      }
    }
  } else {
    return String(toPrimitive(value));
  }
}

export function emissionsFormatColumnName(
  chemicalsByCasOrCategory: Partial<Record<CasOrCategory, ChemicalBrief>>,
  columnID: ColumnID,
): string {
  const columnAsCas = columnID as unknown as CasOrCategory;
  if (standardPollutantLabels.has(columnAsCas)) {
    return standardPollutantLabels.get(columnAsCas)!;
  } else if (chemicalsByCasOrCategory[columnAsCas]) {
    return `${chemicalsByCasOrCategory[columnAsCas]!.name} (${columnID})`;
  } else {
    return columnID;
  }
}
